import React, { useState, useMemo, useEffect } from "react";
import { LeadViewDetailsViewFullInfoProps } from "./lead-view-details-view-full-info.props";
import "./lead-view-details-view-full-info.scss";
import LeadsStatus from "../../../../leads-status";
import { formatDateOnly, formatDateTime, isValidPhoneNumber } from "../../../../../../utils/utils";
import ContactLink from "../../../../../../common/components/contact-link/contact-link";
import { useLogin } from "../../../../../../common/hooks/useUser";
import { useResourcePermissions } from "../../../../../../common/hooks/resource-permissions";
import RenderCellContent from "../../../render-cell-content";
import MODULES from "../../../../../../constants/modules";
import CallOverDuePopup from "../../../../../../common/components/call-over-due-popup/call-over-due-popup";
import {
  editCall,
  fetchOverDueCallsWithLeadId,
} from "../../../../../../api/campaign";
import { AlertComponent } from "../../../../../../common/components";
import { useSetLoading } from "../../../../../../common/hooks/appearance";
import { convertToMinutes } from "../../../../../../utils/utils";

const LeadViewDetailsViewFullInfo: React.FC<
  LeadViewDetailsViewFullInfoProps
> = (props: any) => {
  const {
    leadDetails,
    viewFullInfoOpen,
    setViewFullInfoOpen,
    permissions,
    isMobile,
  } = props;
  const [isViewOpen, setIsViewOpen] = useState<boolean>(true);
  const handleEditOpen = () => setViewFullInfoOpen(!viewFullInfoOpen);
  const { tenantDetails } = useLogin();
  const [selectedCallStatus, setSelectedCallStatus] = useState<string>("");
  const [isOverDueCall, setIsOverDueCall] = useState<boolean>(false);
  const [initialCall, setInitialCall] = useState<any>();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);

  const checkIsDisableEdit = () => {
    return tenantDetails?.tenantType === "Tenant" &&
      leadDetails?.lead_status === "Closed-Lost" &&
      leadDetails?.lead_source === "AFP"
      ? true
      : false;
  };

  const preparePayload = (callStatus: string) => {
    const payload: any = {
      callFor: initialCall?.callFors?.map((opt: any) => ({
        id: opt?.id,
        resource: opt?.resource,
      })),
      relatedToLeadId: leadDetails?.id || null,
      callType: initialCall?.call_type || null,
      outGoingCallStatus: callStatus || null,
      callStartTime: initialCall?.call_start_time || null,
      ownerId: Number(initialCall.call_owner_id) || null,
      subject: initialCall.subject || null,
      callPurpose: initialCall.call_purpose || null,
      callAgenda: initialCall?.call_agenda || null,
      notes: initialCall?.notes || null, // Use initialCall?.notes if available
      cancelNotes: initialCall?.cancel_notes || null, // Use initialCall?.cancel_notes
    };

    if (initialCall?.call_duration) {
      payload.callResult = initialCall.call_result || null;
      payload.description = initialCall.description || null;
      payload.callDuration = initialCall?.call_duration || null;
    }

    return payload;
  };

  const parseLeadNotes = (notes: string) => {
    if (!notes) return [];

    return notes.split("\n").map((line) => {
      const colonIndex = line.indexOf(":");

      if (colonIndex === -1) {
        return {
          title: "",
          value: line.trim(),
        };
      }

      return {
        title: line.slice(0, colonIndex).trim(),
        value: line.slice(colonIndex + 1).trim(),
      };
    });
  };


  const saveCall = async (payload: any) => {
    try {
      setLoading(true);
      let response;
      if (initialCall?.id) {
        response = await editCall(initialCall.id, payload);
        if (response?.status === 200) {
          setAlertMessage(
            `Call ${payload?.outGoingCallStatus?.toLowerCase()} successfully`
          );
          setAlertType("success");
          setAlertOpen(true);
          setIsOverDueCall(false);
        }
      }
    } catch (error: any) {
      console.error("Error saving call:", error);

      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to save call. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCallOptionChange = async (callStatus: string) => {
    let newCallStatus = "Scheduled";
    if (callStatus === "Mark As Completed") {
      newCallStatus = "Completed";
    } else if (callStatus === "Cancel Call") {
      newCallStatus = "Cancelled";
    }
    console.log("Selected Call Status:", newCallStatus);
    const payload = preparePayload(newCallStatus);
    await saveCall(payload);
  };

  useEffect(() => {
    if (leadDetails?.id) {
      const fetchOverDueCalls = async () => {
        try {
          setLoading(true);

          const response = await fetchOverDueCallsWithLeadId(leadDetails.id);
          if (response?.data?.data?.id) {
            setInitialCall(response?.data?.data);
            setIsOverDueCall(true);
          } else {
            setIsOverDueCall(false);
          }
        } catch (error) {
          console.error("Error fetching overdue calls:", error);
          setIsOverDueCall(false);
        } finally {
          setLoading(false);
        }
      };
      fetchOverDueCalls();
    }
  }, [leadDetails]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="lead-list-view-details-view-box-full-info">
      {!isMobile && (
        <div className="lead-list-view-details-view-box-full-info-header">
          <div>Details</div>
          <div>
            {isViewOpen ? (
              <i
                className="fa fa-angle-down"
                onClick={() => {
                  setIsViewOpen(!isViewOpen);
                }}
              />
            ) : (
              <i
                className="fa fa-angle-up"
                onClick={() => {
                  setIsViewOpen(!isViewOpen);
                }}
              />
            )}
          </div>
        </div>
      )}

      {isViewOpen ? (
        <>
          <div className="lead-list-view-details-view-box-full-info-header-text-edit">
            <div className="lead-list-view-details-view-box-full-info-header-text">
              Lead Information
            </div>
            <div className="lead-list-view-details-view-box-full-info-header-edit">
              <button
                className="lead-list-view-edit-section"
                onClick={handleEditOpen}
                disabled={
                  checkIsDisableEdit() ||
                  !permissions?.canWrite ||
                  leadDetails?.lead_status === "Closed-Won"
                }
              >
                Edit
              </button>
            </div>
          </div>
          <div className="lead-list-view-details-view-box-full-info-data">
            <div className="lead-list-view-details-view-box-full-info-data-section">
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  First Name
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  <RenderCellContent text={leadDetails?.first_name || "-"} />
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Last Name
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  <RenderCellContent text={leadDetails?.last_name || "-"} />
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Lead Source
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.lead_source || "-"}
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Primay Phone Number
                </div>
                <ContactLink
                  type="Phone"
                  id={leadDetails?.id}
                  value={
                    leadDetails?.primary_phone_number &&
                    isValidPhoneNumber(leadDetails.primary_phone_number)
                      ? leadDetails.primary_phone_number
                      : "-"
                  }
                  className="lead-list-view-details-view-box-full-info-data-section-sub"
                />
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Modified By
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.modified_by || "-"}
                  <br />
                  {formatDateTime(leadDetails?.updated_at || "-", true)}
                </div>
              </div>
              {tenantDetails?.tenantType !== "Tenant" && (
                <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                  <div className="lead-list-view-details-view-box-full-info-data-section-main">
                    Assigned Partner
                  </div>
                  <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                    {leadDetails?.assignedTo?.company_name || "-"}
                  </div>
                </div>
              )}
            </div>
            <div className="lead-list-view-details-view-box-full-info-data-section">
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Middle Name
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  <RenderCellContent text={leadDetails?.middle_name || "-"} />
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Lead Owner
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.owner?.first_name || ""}{" "}
                  {leadDetails?.owner?.last_name || ""}
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Email
                </div>
                <ContactLink
                  type="Email"
                  id={leadDetails?.id}
                  value={leadDetails?.primary_email_address || "-"}
                  className="lead-list-view-details-view-box-full-info-data-section-sub"
                />
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Interested Product
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.interested_products || "-"}
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Confidence Score
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.confidence_score || "-"}
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Lead Status
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  <LeadsStatus status={leadDetails?.lead_status || "-"} />
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Death Date
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.death_date
                    ? formatDateOnly(leadDetails?.death_date)
                    : "-"}
                </div>
              </div>
            </div>
          </div>
          <div className="lead-list-view-details-view-box-full-info-header-text">
            Address
          </div>
          <div className="lead-list-view-details-view-box-full-info-data">
            <div className="lead-list-view-details-view-box-full-info-data-section">
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Street Line 1
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  <RenderCellContent
                    text={leadDetails?.address?.street_line_1 || "-"}
                  />
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Street Line 2
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  <RenderCellContent
                    text={leadDetails?.address?.street_line_2 || "-"}
                  />
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  State
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.address?.state || "-"}
                </div>
              </div>
            </div>

            <div className="lead-list-view-details-view-box-full-info-data-section">
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Country
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.address?.country || "-"}
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  City
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.address?.city || "-"}
                </div>
              </div>
              <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                <div className="lead-list-view-details-view-box-full-info-data-section-main">
                  Zip Code
                </div>
                <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                  {leadDetails?.address?.zipcode || "-"}
                </div>
              </div>
            </div>
          </div>
          <div className="lead-list-view-details-view-box-full-info-header-text">
            Description
          </div>
          <div className="lead-list-view-details-view-box-full-info-description-box description-box-container">
            {leadDetails?.description || "-"}
          </div>
          {leadDetails?.leadNotes?.length > 0 && (
            <>
              <div className="lead-list-view-details-view-box-full-info-header-text">
                Notes
              </div>
              <div className="lead-list-view-details-view-box-full-info-description-box description-box-container">
                {leadDetails?.leadNotes?.length
                  ? leadDetails.leadNotes.map((noteObj: any) => {
                      const parsedNotes = parseLeadNotes(noteObj.notes);

                      return (
                        <div key={noteObj.id} className="lead-notes-wrapper">
                          {parsedNotes.map((item, index) => (
                            <div key={index} className="lead-note-item">
                              {item.title && (
                                <div className="lead-note-title">
                                  {item.title}
                                </div>
                              )}
                              <div className="lead-note-value">
                                {item.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })
                  : "-"}
              </div>
            </>
          )}
        </>
      ) : (
        ""
      )}
      <CallOverDuePopup
        open={isOverDueCall}
        title={`${convertToMinutes(initialCall?.delayed)} minutes late for the call.`}
        onCancel={() => {
          setIsOverDueCall(false);
        }}
        onOkay={handleCallOptionChange}
        selectedCallStatus={selectedCallStatus}
        setSelectedCallStatus={setSelectedCallStatus}
        details={leadDetails}
        isCustomer={false}
      />
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default LeadViewDetailsViewFullInfo;
